import { getQueryKey } from '@trpc/react-query';
import { BANK_RECONCILIATION } from 'adminConstants/routes';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';

type BankAccount = {
  id: string;
  vehicleId: string;
  bankFeedAccountId?: string | null;
  bankFeedInstitutionId?: string | null;
};

export default function useManageBankAccount(
  selectedBankAccount?: BankAccount,
) {
  const { error } = useNotification();

  const importOperations = api.bankAccount.importOperations.useMutation(
    useHandleTrpcMutation()({
      invalidations: [getQueryKey(api.bankAccount.listBankAccountOperations)],
    }),
  );

  const deactivateBankFeed = api.bankFeed.deactivate.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Bank feed deactivated successfully.',
    }),
  );

  const deactivateBankAccount =
    api.bankAccount.deactivateBankAccount.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Bank account deactivated.',
        invalidations: [getQueryKey(api.bankAccount.listBankAccounts)],
      }),
    );

  const reactivateBankAccount =
    api.bankAccount.reactivateBankAccount.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Bank account reactivated.',
        invalidations: [getQueryKey(api.bankAccount.listBankAccounts)],
      }),
    );

  const deleteBankAccount = api.bankAccount.deleteBankAccount.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Bank account deleted.',
      invalidations: [getQueryKey(api.bankAccount.listBankAccounts)],
    }),
  );

  const authoriseBank = api.bankFeed.authoriseBank.useMutation({
    onSuccess: (data) => {
      if (!data) {
        error('Failed to authorise bank.');
        return;
      }

      window.location.href = data.redirectUrl;
    },
  });

  const handleRefreshBankFeed = () => {
    if (!selectedBankAccount?.bankFeedAccountId) return;

    importOperations.mutate({
      bankAccountId: selectedBankAccount.id,
      vehicleId: selectedBankAccount.vehicleId,
    });
  };

  const handleDeactivateBankFeed = () => {
    if (!selectedBankAccount?.bankFeedAccountId) return;

    deactivateBankFeed.mutate({
      bankAccountId: selectedBankAccount.id,
      vehicleId: selectedBankAccount.vehicleId,
    });
  };

  const handleRenewBankConnection = () => {
    if (
      !selectedBankAccount?.bankFeedAccountId ||
      !selectedBankAccount.bankFeedInstitutionId
    )
      return;

    authoriseBank.mutate({
      institutionId: selectedBankAccount.bankFeedInstitutionId,
      redirectUrl: `${window.location.protocol}//${window.location.host}${BANK_RECONCILIATION}?drawer=processBankAccount&bankAccountId=${selectedBankAccount.id}`,
    });
  };

  const handleDeactivateBankAccount = () => {
    if (!selectedBankAccount) return;

    deactivateBankAccount.mutate({
      bankAccountId: selectedBankAccount.id,
      vehicleId: selectedBankAccount.vehicleId,
    });
  };

  const handleReactivateBankAccount = () => {
    if (!selectedBankAccount) return;

    reactivateBankAccount.mutate({
      bankAccountId: selectedBankAccount.id,
      vehicleId: selectedBankAccount.vehicleId,
    });
  };

  const handleDeleteBankAccount = () => {
    if (!selectedBankAccount) return;

    deleteBankAccount.mutate({
      bankAccountId: selectedBankAccount.id,
      vehicleId: selectedBankAccount.vehicleId,
    });
  };

  return {
    refreshBankFeed: handleRefreshBankFeed,
    deactivateBankFeed: handleDeactivateBankFeed,
    renewBankConnection: handleRenewBankConnection,
    deactivateBankAccount: handleDeactivateBankAccount,
    reactivateBankAccount: handleReactivateBankAccount,
    deleteBankAccount: handleDeleteBankAccount,
    isRefreshingBankFeed: importOperations.isPending,
    isPending:
      importOperations.isPending ||
      deactivateBankFeed.isPending ||
      deactivateBankAccount.isPending ||
      reactivateBankAccount.isPending ||
      deleteBankAccount.isPending,
  };
}
